import api from '@/utils/axios';

function unwrapList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object' && 'data' in payload) {
    const inner = (payload as { data: unknown }).data;
    if (Array.isArray(inner)) return inner;
  }
  return [];
}

function unwrapItem<T>(payload: unknown): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

export interface AdminStats {
  total_users: number;
  active_users: number;
  pending_users: number;
  suspended_users: number;
  active_drivers: number;
  pending_documents: number;
  total_zones: number;
}

export interface AdminUser {
  id: number;
  firstnames: string;
  lastname: string;
  email: string | null;
  phone: string;
  status: string;
  types?: Array<{ id: number; name: string }>;
  zone?: { id: number; name: string };
}

export interface AdminDocument {
  id: number;
  user_id: number;
  category_id: number;
  file_url: string;
  status: string;
  started_at: string;
  ended_at: string;
  category?: { id: number; name: string; type?: string };
  user?: AdminUser;
}

export interface ConfigZone {
  id: number;
  name: string;
}

export interface ConfigCategory {
  id: number;
  name: string;
  type: string;
}

export interface ConfigReference {
  id: number;
  name: string;
}

export interface ConfigType {
  id: number;
  name: string;
}

export interface UserType {
  id: number;
  name: string;
}

export interface UserPermission {
  id: number;
  name: string;
}

export async function fetchAdminStats(): Promise<AdminStats> {
  const [users, documents, zones] = await Promise.all([
    fetchAllUsers(),
    fetchAllDocuments(),
    fetchZones(),
  ]);

  const drivers = users.filter((u) =>
    u.types?.some((t) => ['community_delivery', 'professional_delivery'].includes(t.name)),
  );

  return {
    total_users: users.length,
    active_users: users.filter((u) => u.status === 'active').length,
    pending_users: users.filter((u) => u.status === 'pending').length,
    suspended_users: users.filter((u) => u.status === 'suspended').length,
    active_drivers: drivers.filter((u) => u.status === 'active').length,
    pending_documents: documents.filter((d) => d.status === 'pending').length,
    total_zones: zones.length,
  };
}

export async function fetchAllUsers(): Promise<AdminUser[]> {
  const res = await api.get('/users');
  return unwrapList<AdminUser>(res.data);
}

export async function fetchUserDetail(userId: number): Promise<AdminUser> {
  const res = await api.get(`/users/${userId}`);
  return unwrapItem<AdminUser>(res.data);
}

export async function updateUserStatus(userId: number, status: string): Promise<AdminUser> {
  const res = await api.patch(`/users/${userId}`, { status });
  return unwrapItem<AdminUser>(res.data);
}

export async function fetchAllDocuments(): Promise<AdminDocument[]> {
  const res = await api.get('/documents');
  return unwrapList<AdminDocument>(res.data);
}

export async function validateDocument(
  documentId: number,
  status: 'valid' | 'dismiss' | 'suspended',
): Promise<AdminDocument> {
  const res = await api.patch(`/documents/${documentId}/validate`, { status });
  return unwrapItem<AdminDocument>(res.data);
}

export async function fetchZones(): Promise<ConfigZone[]> {
  const res = await api.get('/configuration/zones/all');
  return unwrapList<ConfigZone>(res.data);
}

export async function createZone(name: string): Promise<ConfigZone> {
  const res = await api.post('/configuration/zones', { name });
  return unwrapItem<ConfigZone>(res.data);
}

export async function updateZone(zoneId: number, name: string): Promise<ConfigZone> {
  const res = await api.patch(`/configuration/zones/${zoneId}`, { name });
  return unwrapItem<ConfigZone>(res.data);
}

export async function deleteZone(zoneId: number): Promise<void> {
  await api.delete(`/configuration/zones/${zoneId}`);
}

export async function fetchCategories(): Promise<ConfigCategory[]> {
  const res = await api.get('/configuration/categories/all');
  return unwrapList<ConfigCategory>(res.data);
}

export async function createCategory(type: string, name: string): Promise<ConfigCategory> {
  const res = await api.post('/configuration/categories', { type, name });
  return unwrapItem<ConfigCategory>(res.data);
}

export async function updateCategory(categoryId: number, data: { type?: string; name?: string }): Promise<ConfigCategory> {
  const res = await api.patch(`/configuration/categories/${categoryId}`, data);
  return unwrapItem<ConfigCategory>(res.data);
}

export async function deleteCategory(categoryId: number): Promise<void> {
  await api.delete(`/configuration/categories/${categoryId}`);
}

export async function fetchVehicleTypes(): Promise<ConfigReference[]> {
  const res = await api.get('/vehicle-types');
  return unwrapList<ConfigReference>(res.data);
}

export async function fetchSpecializations(): Promise<ConfigReference[]> {
  const res = await api.get('/specializations');
  return unwrapList<ConfigReference>(res.data);
}

export async function fetchAvailabilitySlots(): Promise<ConfigReference[]> {
  const res = await api.get('/availability-slots');
  return unwrapList<ConfigReference>(res.data);
}

export async function createSpecialization(name: string): Promise<ConfigReference> {
  const res = await api.post('/specializations', { name });
  return unwrapItem<ConfigReference>(res.data);
}

export async function createAvailabilitySlot(name: string): Promise<ConfigReference> {
  const res = await api.post('/availability-slots', { name, is_week_based: false });
  return unwrapItem<ConfigReference>(res.data);
}

export async function createVehicleType(name: string): Promise<ConfigReference> {
  const res = await api.post('/vehicle-types', { name });
  return unwrapItem<ConfigReference>(res.data);
}

export async function updateVehicleType(id: number, name: string): Promise<ConfigReference> {
  const res = await api.patch(`/vehicle-types/${id}`, { name });
  return unwrapItem<ConfigReference>(res.data);
}

export async function deleteVehicleType(id: number): Promise<void> {
  await api.delete(`/vehicle-types/${id}`);
}

export async function updateSpecialization(id: number, name: string): Promise<ConfigReference> {
  const res = await api.patch(`/specializations/${id}`, { name });
  return unwrapItem<ConfigReference>(res.data);
}

export async function deleteSpecialization(id: number): Promise<void> {
  await api.delete(`/specializations/${id}`);
}

export async function updateAvailabilitySlot(id: number, name: string): Promise<ConfigReference> {
  const res = await api.patch(`/availability-slots/${id}`, { name });
  return unwrapItem<ConfigReference>(res.data);
}

export async function deleteAvailabilitySlot(id: number): Promise<void> {
  await api.delete(`/availability-slots/${id}`);
}

// Configuration Types CRUD
export async function fetchTypes(): Promise<ConfigType[]> {
  const res = await api.get('/configuration/types/all');
  return unwrapList<ConfigType>(res.data);
}

export async function createType(name: string): Promise<ConfigType> {
  const res = await api.post('/configuration/types', { name });
  return unwrapItem<ConfigType>(res.data);
}

export async function updateType(id: number, name: string): Promise<ConfigType> {
  const res = await api.patch(`/configuration/types/${id}`, { name });
  return unwrapItem<ConfigType>(res.data);
}

export async function deleteType(id: number): Promise<void> {
  await api.delete(`/configuration/types/${id}`);
}

// User Types CRUD
export async function fetchUserTypes(userId: number): Promise<UserType[]> {
  const res = await api.get(`/configuration/users/${userId}/types`);
  return unwrapList<UserType>(res.data);
}

export async function addUserType(userId: number, typeId: number): Promise<UserType> {
  const res = await api.post(`/configuration/users/${userId}/types`, { type_id: typeId });
  return unwrapItem<UserType>(res.data);
}

export async function removeUserType(userId: number, typeId: number): Promise<void> {
  await api.delete(`/configuration/users/${userId}/types/${typeId}`);
}

// User Permissions CRUD
export async function fetchUserPermissions(userId: number): Promise<UserPermission[]> {
  const res = await api.get(`/configuration/users/${userId}/permissions`);
  return unwrapList<UserPermission>(res.data);
}

export async function addUserPermission(userId: number, permissionId: number): Promise<UserPermission> {
  const res = await api.post(`/configuration/users/${userId}/permissions`, { permission_id: permissionId });
  return unwrapItem<UserPermission>(res.data);
}

export async function removeUserPermission(userId: number, permissionId: number): Promise<void> {
  await api.delete(`/configuration/users/${userId}/permissions/${permissionId}`);
}
